// Winback (OptiReach) reactivation UTMs. The OptiReach email CTA lands on
// `/signIn?utm_source=email&utm_medium=optireach&utm_campaign=<campaign>&utm_content=<company_id>`;
// `utm_medium=optireach` is THE gate the WinbackTrialBanner and the
// `reactivateWithTrial` mutation key on.
//
// The 5-tuple is persisted to **localStorage** with a 14-day TTL, not to
// sessionStorage: the forgot-password detour (CTA → "Forgot Password?" → reset
// email → /password/reset → "Continue to Sign In" → sign in) normally opens the
// reset link in a NEW TAB, and a new tab starts an empty sessionStorage — so a
// session-scoped capture was lost exactly in the flow that needs it most.
// localStorage is shared across every tab of the browser profile, so the
// capture is still there when the user comes back to /signIn.
//
// Consequences of the wider scope, accepted deliberately:
//  - The window is bounded by WINBACK_UTM_TTL_DAYS and expired entries are
//    PURGED on read, not merely ignored.
//  - The capture is cleared as soon as the server gives a definitive answer to a
//    claim — granted OR rejected (LoginPage `claimWinbackTrial`,
//    InactiveAccountDialog `handleClaimTrial`) — so a spent capture can never
//    re-fire the mutation on later sign-ins inside the window.
//  - Still scoped to one browser profile: a cross-DEVICE detour (email on the
//    phone, portal on the desktop) cannot be bridged here.

import { type SignupUtmData } from './utm-capture';
import { safeJsonParse } from './safe-format';

export const WINBACK_UTM_STORAGE_KEY = 'os_winback_utm';

export const WINBACK_UTM_MEDIUM = 'optireach';

/** How long a capture stays claimable — covers the forgot-password detour. */
export const WINBACK_UTM_TTL_DAYS = 14;

const TTL_MS = WINBACK_UTM_TTL_DAYS * 24 * 60 * 60 * 1000;

/** Stored envelope: the tuple plus the capture time the TTL is measured from. */
interface StoredWinbackUtm {
  utm: SignupUtmData;
  capturedAt: number;
}

/** A utm 5-tuple qualifies for winback only with the optireach gate + a campaign. */
function isWinbackUtm(utm: SignupUtmData | null | undefined): utm is SignupUtmData {
  return utm?.utmMedium === WINBACK_UTM_MEDIUM && Boolean(utm.utmCampaign);
}

/**
 * Winback UTMs from the CURRENT URL only — deliberately NOT the shared
 * `getSignupUtmData()` chain (URL → document.referrer → `os_utm` cookie →
 * PostHog `initial_utm_*`). PostHog's first-touch properties never expire, so
 * that chain lets a months-old optireach click re-mint a fresh capture on any
 * /signIn visit: it would defeat WINBACK_UTM_TTL_DAYS and offer (then grant)
 * the trial to someone who never clicked a winback email. The OptiReach CTA
 * always lands with the params in the URL — the link targets
 * app.optisigns.com/signIn directly, with no Webflow hop that could strip them
 * — so the URL is the only trustworthy source here.
 *
 * Values are capped at 100 chars to match `UtmDataInput`'s @MaxLength(100);
 * a longer value would fail mutation validation instead of granting.
 */
function readUrlWinbackUtm(): SignupUtmData | null {
  let params: URLSearchParams;
  try {
    params = new URL(window.location.href).searchParams;
  } catch {
    return null;
  }
  const pick = (key: string): string | undefined => {
    const value = params.get(key)?.trim();
    return value ? value.slice(0, 100) : undefined;
  };
  const utm: SignupUtmData = {
    utmSource: pick('utm_source'),
    utmMedium: pick('utm_medium'),
    utmCampaign: pick('utm_campaign'),
    utmContent: pick('utm_content'),
    utmTerm: pick('utm_term'),
  };
  return isWinbackUtm(utm) ? utm : null;
}

// localStorage access itself throws when site data is blocked (Safari "Block All
// Cookies", hardened privacy modes) — and both the sign-in banner and the
// paywall dialog read this during render, so an unguarded throw would blank the
// page instead of just skipping the offer.
function readRaw(): string | null {
  try {
    return localStorage.getItem(WINBACK_UTM_STORAGE_KEY);
  } catch {
    return null;
  }
}

function writeRaw(entry: StoredWinbackUtm): void {
  try {
    localStorage.setItem(WINBACK_UTM_STORAGE_KEY, JSON.stringify(entry));
  } catch {
    // Storage unavailable / quota — the value returned to the caller still
    // serves the current page; only cross-tab persistence is lost.
  }
}

function removeRaw(): void {
  try {
    localStorage.removeItem(WINBACK_UTM_STORAGE_KEY);
  } catch {
    // Nothing to do — a capture we cannot clear is bounded by the TTL anyway.
  }
}

/**
 * Resolve the winback UTMs without persisting: the current URL wins when it
 * passes the optireach gate, else a previously stored capture. Null when
 * neither qualifies.
 */
export function getWinbackUtm(): SignupUtmData | null {
  return readUrlWinbackUtm() ?? getStoredWinbackUtm();
}

/**
 * Resolve AND persist the winback UTMs so later surfaces (the new tab opened by
 * the password-reset email, the MFA return, InactiveAccountDialog) can read the
 * 5-tuple after the URL params are gone. Returns the resolved tuple, or null
 * when nothing qualifies.
 *
 * Only a fresh URL touch (re)starts the TTL — falling back to a stored capture
 * never extends it, so a 14-day-old click cannot be renewed by revisiting
 * /signIn.
 */
export function captureWinbackUtm(): SignupUtmData | null {
  const current = readUrlWinbackUtm();
  if (current) {
    writeRaw({ utm: current, capturedAt: Date.now() });
    return current;
  }
  return getStoredWinbackUtm();
}

/**
 * Read a previously captured winback 5-tuple. Returns null — and purges the
 * entry — when it is absent, unparseable, un-gated, or older than
 * WINBACK_UTM_TTL_DAYS, so a stale capture never lingers in the browser.
 */
export function getStoredWinbackUtm(): SignupUtmData | null {
  const raw = readRaw();
  if (!raw) return null;
  const parsed = safeJsonParse<StoredWinbackUtm | null>(raw, null);
  const utm = parsed?.utm;
  const capturedAt = parsed?.capturedAt;
  // Anything we can't validate is treated as expired: drop it rather than
  // leaving an un-claimable entry behind (this also drops the pre-TTL
  // sessionStorage-era shape, which carried no capturedAt).
  if (!isWinbackUtm(utm) || typeof capturedAt !== 'number' || !Number.isFinite(capturedAt)) {
    removeRaw();
    return null;
  }
  if (Date.now() - capturedAt > TTL_MS) {
    removeRaw();
    return null;
  }
  return utm;
}

/**
 * Clear the capture. Called once the server has given a definitive answer to a
 * claim — granted OR rejected — so a spent capture never re-fires the mutation
 * for the rest of the TTL window.
 */
export function clearWinbackUtm(): void {
  removeRaw();
}
